/**
 * RibbonToolbar
 *
 * Excel-style ribbon toolbar with four tabs:
 *   File        → dropdown menu only (Save / Save as New Version)
 *   Graph View  → ribbon with grouped controls (default active)
 *   Data        → ribbon with DoE / Batch / Lock Parameters
 *   Review      → ribbon with Simulate
 */

import React, { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import CircularProgress from '@mui/material/CircularProgress';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import { ThemeProvider } from '@mui/material/styles';

// Icons
import SaveIcon from '@mui/icons-material/Save';
import SaveAsIcon from '@mui/icons-material/SaveAs';
import ForkRightIcon from '@mui/icons-material/ForkRight';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import { theme } from '@/theme';
import { useGraphEditorStore } from '@/graph-store';
import { LockToggleButton } from './LockToggleButton';
import type { GraphEditorToolbarProps } from './types';

// =============================================================================
// Ribbon group layout primitives
// =============================================================================

interface RibbonGroupProps {
  label: string;
  children: React.ReactNode;
  /** hide the right-side divider when true */
  last?: boolean;
}

const RibbonGroup: React.FC<RibbonGroupProps> = ({ label, children, last }) => (
  <Box
    sx={{
      display: 'flex',
      flexShrink: 0,
    }}
  >
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        px: 1,
        py: 0.75,
        minWidth: 120,
      }}
    >
      {/* Controls row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 0.5,
          flexWrap: 'nowrap',
          mb: 0.5,
        }}
      >
        {children}
      </Box>
      {/* Label */}
      {/* <Typography
        variant="caption"
        sx={{
          fontSize: '0.65rem',
          color: 'text.secondary',
          userSelect: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography> */}
    </Box>
    {!last && (
      <Divider
        orientation="vertical"
        flexItem
        sx={{ my: 0.5 }}
      />
    )}
  </Box>
);

// =============================================================================
// Graph View ribbon
// =============================================================================

interface GraphViewRibbonProps {
  onCollapseGroups: () => void;
  onExpandGroups: () => void;
  collapseGroupsLabel?: string;
  expandGroupsLabel?: string;
  isProtocolLocked?: boolean;
  isLifecycleLocked?: boolean;
  canToggleLock?: boolean;
  lockReason?: string | null;
  isLockLoading?: boolean;
  onToggleLock?: () => void;
}

/** A labelled toggle row: icon + text on the left, Switch on the right */
interface RibbonToggleRowProps {
  icon: React.ReactNode;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const RibbonToggleRow: React.FC<RibbonToggleRowProps> = ({ icon, label, checked, onChange }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      width: '100%',
      px: 0.5,
      py: 0.25,
      borderRadius: 0.5,
      cursor: 'pointer',
      '&:hover': { backgroundColor: 'action.hover' },
    }}
    onClick={() => onChange(!checked)}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', color: checked ? 'primary.main' : 'text.secondary' }}>
      {icon}
    </Box>
    <Typography
      variant="caption"
      sx={{
        flex: 1,
        fontSize: '0.72rem',
        fontWeight: checked ? 600 : 400,
        color: checked ? 'text.primary' : 'text.secondary',
        userSelect: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
   <Switch
    size="small"
    checked={checked}
    onClick={(e) => e.stopPropagation()}
    onChange={(e) => onChange(e.target.checked)}
    sx={{ ml: 0.5 }}
/>
  </Box>
);

const GraphViewRibbon: React.FC<GraphViewRibbonProps> = ({
  onCollapseGroups,
  onExpandGroups,
  collapseGroupsLabel,
  expandGroupsLabel,
  isProtocolLocked = false,
  isLifecycleLocked = false,
  canToggleLock = false,
  lockReason = null,
  isLockLoading = false,
  onToggleLock,
}) => {
  const [graphViewEnabled, setGraphViewEnabled] = useState(true);
  
  // Get highlight state from Zustand store
  const highlights = useGraphEditorStore((s) => s.highlights.visible);
  const toggleHighlightVisible = useGraphEditorStore((s) => s.toggleHighlightVisible);

  const [containerImages, setContainerImages] = useState(false);

  const toggle = (key: keyof typeof highlights) =>
    toggleHighlightVisible(key);

  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
      {/* Group 1 – Graph View toggle */}


      {/* Group 2 – Groups */}
      <RibbonGroup label="Groups">
        <Button
          size="small"
          color="inherit"
          startIcon={<UnfoldLessIcon sx={{ fontSize: 15 }} />}
          onClick={onCollapseGroups}
          sx={{
            textTransform: 'none',
            fontSize: '0.72rem',
            justifyContent: 'flex-start',
            width: '100%',
            color: 'text.primary',
            whiteSpace: 'nowrap',
          }}
        >
          {collapseGroupsLabel ?? 'Collapse All'}
        </Button>
        <Button
          size="small"
          color="inherit"
          startIcon={<UnfoldMoreIcon sx={{ fontSize: 15 }} />}
          onClick={onExpandGroups}
          sx={{
            textTransform: 'none',
            fontSize: '0.72rem',
            justifyContent: 'flex-start',
            width: '100%',
            color: 'text.primary',
            whiteSpace: 'nowrap',
          }}
        >
          {expandGroupsLabel ?? 'Expand All'}
        </Button>
      </RibbonGroup>

      {/* Group 3 – Highlights */}
      <RibbonGroup label="Highlights">
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, alignItems: 'center' }}>
          <RibbonToggleRow
            icon={<CommentOutlinedIcon sx={{ fontSize: 15 }} />}
            label="Comments"
            checked={highlights.comments}
            onChange={() => toggle('comments')}
          />
          <RibbonToggleRow
            icon={<NoteAltOutlinedIcon sx={{ fontSize: 15 }} />}
            label="Notes"
            checked={highlights.notes}
            onChange={() => toggle('notes')}
          />
          <RibbonToggleRow
            icon={<FlagOutlinedIcon sx={{ fontSize: 15 }} />}
            label="Checkpoints"
            checked={highlights.checkpoints}
            onChange={() => toggle('checkpoints')}
          />
          <RibbonToggleRow
            icon={<NotificationsNoneIcon sx={{ fontSize: 15 }} />}
            label="Notifications"
            checked={highlights.notifications}
            onChange={() => toggle('notifications')}
          />
          <RibbonToggleRow
            icon={<CheckCircleOutlineIcon sx={{ fontSize: 15 }} />}
            label="Approvals"
            checked={highlights.approvals}
            onChange={() => toggle('approvals')}
          />
        </Box>
      </RibbonGroup>

      {/* Group 4 – Lock */}
      <RibbonGroup label="Protocol">
        <LockToggleButton
          isLocked={isProtocolLocked || isLifecycleLocked}
          isDisabled={!canToggleLock}
          lockReason={lockReason}
          onToggle={onToggleLock || (() => {})}
          isLoading={isLockLoading}
        />
      </RibbonGroup>

      {/* Group 5 – Misc */}
      {/* <RibbonGroup label="Misc" last>
        <RibbonToggleRow
          icon={<ImageOutlinedIcon sx={{ fontSize: 15 }} />}
          label="Container Images"
          checked={containerImages}
          onChange={setContainerImages}
        />
      </RibbonGroup> */}
    </Box>
  );
};

// =============================================================================
// Data ribbon
// =============================================================================

const DataRibbon: React.FC = () => (
  <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', height: 56 }}>
    <RibbonGroup label="Data" last>
      <Button size="small" variant="outlined" sx={{ textTransform: 'none', minWidth: 'unset', px: 1, py: 0.5, fontSize: '0.75rem' }}>
        DoE
      </Button>
      <Button size="small" variant="outlined" sx={{ textTransform: 'none', minWidth: 'unset', px: 1, py: 0.5, fontSize: '0.75rem' }}>
        Batch
      </Button>
      <Button size="small" variant="outlined" sx={{ textTransform: 'none', minWidth: 'unset', px: 1, py: 0.5, fontSize: '0.75rem' }}>
        <LockOutlinedIcon sx={{ fontSize: 14, mr: 0.5 }} />
        Lock Parameters
      </Button>
    </RibbonGroup>
  </Box>
);

// =============================================================================
// Review ribbon
// =============================================================================

const ReviewRibbon: React.FC = () => (
  <Box sx={{ display: 'flex', alignItems: 'stretch', height: 56 }}>
    <RibbonGroup label="Simulation" last>
      <Button
        size="small"
        variant="contained"
        startIcon={<PlayArrowIcon sx={{ fontSize: 14 }} />}
        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
      >
        Simulate
      </Button>
    </RibbonGroup>
  </Box>
);

// =============================================================================
// RibbonToolbar (main export)
// =============================================================================

type RibbonTab = 'graph-view' | 'data' | 'review';

export const RibbonToolbar: React.FC<GraphEditorToolbarProps> = ({
  isDirty,
  isLocked,
  isArchived,
  isArchivalInProgress,
  isLaunching,
  canEditContent,
  isProtocolLocked = false,
  isLifecycleLocked = false,
  canToggleLock = false,
  lockReason = null,
  isLockLoading = false,
  allowVersionCreationDisabled = false,
  permissions,
  onSave,
  onSaveAsNewVersion,
  onSaveAsNewWorkflow,
  onLaunchRun,
  onImportMaterial,
  onImportWorkflow,
  onCollapseGroups = () => {},
  onExpandGroups = () => {},
  collapseGroupsLabel,
  expandGroupsLabel,
  onToggleLock,
}) => {
  // Active ribbon tab — File does not activate a tab
  const [activeTab, setActiveTab] = useState<RibbonTab>('graph-view');

  // File dropdown
  const [fileAnchor, setFileAnchor] = useState<null | HTMLElement>(null);
  const [fileMenuPos, setFileMenuPos] = useState<{ top: number; left: number } | null>(null);
  const fileOpen = Boolean(fileAnchor);

  const handleFileClick = (e: React.MouseEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFileMenuPos({ top: rect.bottom, left: rect.left });
    setFileAnchor(e.currentTarget);
  };

  const handleTabChange = (_: React.SyntheticEvent, value: RibbonTab) => {
    setActiveTab(value);
  };

  const saveDisabled = !permissions.canSave || !isDirty || isLocked || Boolean(isArchived) || Boolean(isArchivalInProgress) || canEditContent === false;
  const saveVersionDisabled = !permissions.canSaveAsNewVersion || Boolean(isArchived) || Boolean(isArchivalInProgress) ;
  const launchDisabled =
    !permissions.canLaunchRun ||
    Boolean(isArchived) ||
    Boolean(isArchivalInProgress) ||
    isLaunching;

  return (
    <ThemeProvider theme={theme}>
      <AppBar
        position="sticky"
        color="default"
        elevation={1}
        sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
      >
        {/* ── Tab row ──────────────────────────────────────────────────── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
            minHeight: 36,
            px: 0.5,
          }}
        >
          {/* File tab – opens dropdown, never changes activeTab */}
          <Button
            size="small"
            onClick={handleFileClick}
            sx={{
              color: fileOpen ? '#fff' : 'text.primary',
              backgroundColor: fileOpen ? 'primary.main' : 'transparent',
              fontWeight: 500,
              px: 1.5,
              py: 0.5,
              minWidth: 'unset',
              textTransform: 'none',
              fontSize: '0.8125rem',
              borderRadius: 0,
              '&:hover': { backgroundColor: fileOpen ? 'primary.main' : 'action.hover' },
            }}
          >
            File
          </Button>

          <Menu
            open={fileOpen}
            onClose={() => setFileAnchor(null)}
            anchorReference="anchorPosition"
            anchorPosition={fileMenuPos ?? { top: 0, left: 0 }}
            marginThreshold={0}
            slotProps={{
              paper: {
                sx: {
                  width: 260,
                  borderRadius: 0,
                  boxShadow: '2px 4px 10px rgba(0,0,0,0.2)',
                  m: 0,
                  backgroundColor: '#fafafa',
                  border: '1px solid #e0e0e0',
                },
              },
              list: {
                sx: { py: 1, pl: 0, pr: 0 },
              },
            }}
          >
            <MenuItem
              disabled={saveDisabled}
              onClick={() => {
                setFileAnchor(null);
                onSave();
              }}
              sx={{ py: 1.25, px: 2 }}
            >
              <ListItemIcon><SaveIcon fontSize="small" /></ListItemIcon>
              <ListItemText slotProps={{ primary: { sx: { fontSize: '0.9rem' } } }}>Save</ListItemText>
            </MenuItem>
            <MenuItem
              disabled={saveVersionDisabled}
              onClick={() => {
                setFileAnchor(null);
                onSaveAsNewVersion();
              }}
              sx={{ py: 1.25, px: 2 }}
            >
              <ListItemIcon><SaveAsIcon fontSize="small" /></ListItemIcon>
              <ListItemText slotProps={{ primary: { sx: { fontSize: '0.9rem' } } }}>Save as New Version</ListItemText>
            </MenuItem>

            <MenuItem
              disabled={!permissions.canSaveAsNewWorkflow || Boolean(isArchived) || Boolean(isArchivalInProgress)}
              onClick={() => {
                setFileAnchor(null);
                onSaveAsNewWorkflow();
              }}
              sx={{ py: 1.25, px: 2 }}
            >
              <ListItemIcon><ForkRightIcon fontSize="small" /></ListItemIcon>
              <ListItemText slotProps={{ primary: { sx: { fontSize: '0.9rem' } } }}>Save As New Procedure</ListItemText>
            </MenuItem>
          </Menu>

          {/* Other tabs */}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons={false}
            sx={{
              minHeight: 36,
              '& .MuiTab-root': {
                minHeight: 36,
                py: 0,
                px: 1.5,
                textTransform: 'none',
                fontSize: '0.8125rem',
                fontWeight: 500,
              },
            }}
          >
            <Tab label="Graph View" value="graph-view" />
            {/* <Tab label="Data" value="data" />
            <Tab label="Review" value="review" /> */}
          </Tabs>

          {/* Workflow phases – centered */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              fontSize: '0.8125rem',
              color: 'text.secondary',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Design
            </Typography>
            <Typography variant="body2" sx={{ mx: 0.5, color: 'text.secondary' }}>
              / Plan / Execute / Analysis
            </Typography>
          </Box>

          <Box sx={{ pr: 0.5 }}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={
                isLaunching ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <PlayArrowIcon sx={{ fontSize: 14 }} />
                )
              }
              disabled={launchDisabled}
              onClick={onLaunchRun}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                minHeight: 28,
                px: 1.25,
              }}
            >
              {isLaunching ? 'Launching...' : 'Launch Run'}
            </Button>
          </Box>
        </Box>

        {/* ── Ribbon content ───────────────────────────────────────────── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1,
            overflow: 'auto',
          }}
        >
          {activeTab === 'graph-view' && (
            <GraphViewRibbon
              onCollapseGroups={onCollapseGroups}
              onExpandGroups={onExpandGroups}
              collapseGroupsLabel={collapseGroupsLabel}
              expandGroupsLabel={expandGroupsLabel}
              isProtocolLocked={isProtocolLocked}
              isLifecycleLocked={isLifecycleLocked}
              canToggleLock={canToggleLock}
              lockReason={lockReason}
              isLockLoading={isLockLoading}
              onToggleLock={onToggleLock}
            />
          )}
          {activeTab === 'data' && <DataRibbon />}
          {activeTab === 'review' && <ReviewRibbon />}
        </Box>
      </AppBar>
    </ThemeProvider>
  );
};
